import React, { useState, useCallback } from 'react';
import { Button, Modal } from 'antd';
import { CheckCircleFilled, CloseOutlined } from '@ant-design/icons';
import { verifyEhrConnection } from 'api/ehr';
import { EHR_CONFIG_MAP, EHR_EXTERNAL_INFO_FIELDS, getEhrSteps } from './ehrConfig';
import useInsuranceBillingStatus from 'hooks/useInsuranceBillingStatus';
import EhrBadge from './EhrBadge';
import EhrStepGuide from './EhrStepGuide';
import VerifyStep from './VerifyStep';
import s from './s.module.less';

type Props = {
    ehrName: string;
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
};

type FlowStep = 'connected' | 'guide' | 'verify' | 'success';

const EhrConnectFlow = ({ ehrName, open, onClose, onSuccess }: Props) => {
    const isKlarity = ehrName.includes('Klarity');
    const [step, setStep] = useState<FlowStep>(isKlarity ? 'connected' : 'guide');
    const [verifying, setVerifying] = useState(false);
    const [billingStatus] = useInsuranceBillingStatus();

    const config = EHR_CONFIG_MAP[ehrName];
    const stepsConfig = getEhrSteps()[ehrName];
    const email = billingStatus?.agentEmail || '';
    const externalInfoFields = EHR_EXTERNAL_INFO_FIELDS[ehrName];
    const verifyType = config?.verifyType || 'agent_auto';
    const isInstant = ehrName === 'Klarity';

    const handleClose = () => {
        setStep(isKlarity ? 'connected' : 'guide');
        setVerifying(false);
        onClose();
    };

    const handleSuccess = () => {
        setStep('success');
    };

    const handleDone = () => {
        setStep(isKlarity ? 'connected' : 'guide');
        onSuccess();
    };

    const doKlarityVerify = useCallback(async () => {
        setVerifying(true);
        try {
            const res = await verifyEhrConnection({ ehrSystem: ehrName });
            if (res && !res.error) {
                const data = res.data?.data;
                if (data?.verified || data?.connectionStatus === 'connected') {
                    setStep('success');
                }
            }
        } finally {
            setVerifying(false);
        }
    }, [ehrName]);

    const handleContinue = () => {
        if (step === 'connected') {
            doKlarityVerify();
            return;
        }
        if (step === 'guide') {
            setStep('verify');
        } else if (step === 'verify') {
            handleDone();
        }
    };

    const handleBack = () => {
        if (step === 'verify') {
            setStep(isKlarity ? 'connected' : 'guide');
        } else {
            handleClose();
        }
    };

    const renderContent = () => {
        if (step === 'success') {
            return (
                <div className={s.successWrap}>
                    <div className={s.successIcon}>
                        <CheckCircleFilled />
                    </div>
                    <h2 className={s.successTitle}>You are all set!</h2>
                    <p className={s.successDesc}>
                        {ehrName} is connected. Klarity is already working in the background.
                    </p>
                    <div className={s.successActions}>
                        <Button onClick={() => { handleDone(); onClose(); }}>
                            Connect another EHR
                        </Button>
                        <Button type="primary" onClick={handleDone}>
                            Done
                        </Button>
                    </div>
                </div>
            );
        }

        if (step === 'connected') {
            return (
                <div className={s.flowContent}>
                    <div className={s.flowInner}>
                        <h2 className={s.flowTitle}>You are already signed in to {ehrName}</h2>
                        <p className={s.flowSubtitle}>
                            There is nothing to connect. Your account is already linked, so you can continue.
                        </p>
                        <div className={s.alreadyConnectedCard}>
                            <div className={s.alreadyConnectedIcon}>
                                <CheckCircleFilled />
                            </div>
                            <div className={s.alreadyConnectedTitle}>Already connected</div>
                            <div className={s.alreadyConnectedDesc}>
                                Your {ehrName} patients and appointments are synced and ready to bill, with no login or password sharing needed.
                            </div>
                        </div>
                        <span className={s.helpLink}>
                            Need help? Email{' '}
                            <a href="mailto:payer@helloklarity.com">payer@helloklarity.com</a>
                        </span>
                    </div>
                </div>
            );
        }

        if (step === 'verify') {
            return (
                <div className={s.flowContent}>
                    <div className={s.flowInner}>
                        <h2 className={s.flowTitle}>Verify your {ehrName} connection</h2>
                        <p className={s.flowSubtitle}>
                            Klarity is checking that it can access your {ehrName} account.
                        </p>
                        <VerifyStep
                            ehrName={ehrName}
                            verifyType={verifyType}
                            stepsConfig={stepsConfig}
                            agentEmail={email}
                            externalInfoFields={externalInfoFields}
                            onSuccess={handleSuccess}
                            onBack={() => setStep(isKlarity ? 'connected' : 'guide')}
                        />
                    </div>
                </div>
            );
        }

        return (
            <div className={s.flowContent}>
                <div className={s.flowInner}>
                    <h2 className={s.flowTitle}>Connect your {ehrName} account</h2>
                    <p className={s.flowSubtitle}>
                        Create an account for Klarity in your EHR so it can access billing, scheduling, and patient data on your behalf.
                    </p>
                    {stepsConfig && (
                        <EhrStepGuide config={stepsConfig} agentEmail={email} />
                    )}
                </div>
            </div>
        );
    };

    let rightLabel = 'Continue';
    if (!isInstant && step !== 'guide') rightLabel = 'Finish';

    return (
        <Modal
            open={open}
            onCancel={handleClose}
            footer={null}
            closable={false}
            width={1000}
            centered
            destroyOnClose
            className={s.flowModal}
        >
            <div className={s.flowWrap}>
                {step !== 'success' ? (
                    <>
                        <div className={s.flowTopBar}>
                            <div className={s.flowTopSpacer} />
                            <div className={s.flowTopCenter}>
                                <EhrBadge ehrName={ehrName} size={32} />
                                <span className={s.flowTopTitle}>Connect {ehrName}</span>
                                {!isInstant && (
                                    <span className={s.flowTopCount}>
                                        &middot; Step {step === 'guide' ? 1 : 2} of 2
                                    </span>
                                )}
                            </div>
                            <Button
                                type="text"
                                icon={<CloseOutlined />}
                                onClick={handleClose}
                                className={s.flowCloseBtn}
                            />
                        </div>
                        {renderContent()}
                        <div className={s.flowFooter}>
                            <div className={s.flowFooterInner}>
                                <Button className={s.flowCancelBtn} onClick={handleClose}>Cancel</Button>
                                <div className={s.flowFootRight}>
                                    <span className={s.flowBackLink} onClick={handleBack}>Back</span>
                                    <Button
                                        type="primary"
                                        className={s.flowContinueBtn}
                                        onClick={handleContinue}
                                        disabled={step === 'verify' && !isInstant}
                                        loading={verifying}
                                    >
                                        {rightLabel}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </>
                ) : (
                    renderContent()
                )}
            </div>
        </Modal>
    );
};

export default EhrConnectFlow;
